import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Button, Drawer, DrawerBody, DrawerContent, DrawerFooter, DrawerHeader, DrawerOverlay, DrawerCloseButton,
  Menu, MenuButton, MenuItemOption, MenuList, MenuOptionGroup, Text, useDisclosure
} from '@chakra-ui/react';
import { ExpandMore, Search as SearchIcon } from '@mui/icons-material';

import Breadcrumb from '../components/layout/Breadcrumb';
import ProductGrid from '../components/product/ProductGrid';
import ProductFilters, { emptyFilters, applyFilters, countActiveFilters, filterGroups, sortSizes } from '../components/ProductFilters';
import { getAllProducts, getProductByCategoryId, getProductBySearch } from '../services/ProductServices';
import useCatalog from '../hooks/useCatalog';

const SORTS = {
  recommended: { label: 'Recommended', fn: null },
  newest: { label: 'Latest', fn: (a, b) => (b._id > a._id ? 1 : -1) },
  price_asc: { label: 'Price: Low to High', fn: (a, b) => a.price - b.price },
  price_desc: { label: 'Price: High to Low', fn: (a, b) => b.price - a.price }
};

const condensedLabel = { textTransform: 'uppercase', fontWeight: 600, sx: { fontStretch: '75%' } };

// Collection + search results (design-system.md §5.3). Driven by router state: { q, categoryId, maxPrice }.
const Search = () => {

  const navigate = useNavigate();
  const { state } = useLocation();
  const q = state?.q || '';
  const categoryId = state?.categoryId || '';
  const maxPrice = state?.maxPrice || null;

  const filterSheet = useDisclosure();
  const sortSheet = useDisclosure();
  const { categories, products: allProducts } = useCatalog();
  const [products, setProducts] = useState(null);
  const [sortBy, setSortBy] = useState('recommended');
  const [filters, setFilters] = useState(emptyFilters([0, 0]));
  // Phone filter drawer edits a draft that applies on APPLY FILTERS
  const [draft, setDraft] = useState(filters);
  const [drawerGroup, setDrawerGroup] = useState(null);

  useEffect(() => {
    setProducts(null);
    const load = categoryId ? getProductByCategoryId(categoryId).then(r => r.products)
      : q ? getProductBySearch(q).then(r => r.products)
        : getAllProducts().then(r => r.allProducts);
    load.then(list => setProducts((list || []).filter(p => p.status))).catch(() => setProducts([]));
  }, [q, categoryId]);

  const priceBounds = useMemo(() => {
    if (!products?.length) return [0, 0];
    const prices = products.map(p => p.price);
    return [Math.floor(Math.min(...prices) / 50) * 50, Math.ceil(Math.max(...prices) / 50) * 50];
  }, [products]);

  // Fresh filters for every new result set; "Under ₹999" pre-sets the price cap
  useEffect(() => {
    const fresh = emptyFilters(priceBounds);
    if (maxPrice && priceBounds[1] > maxPrice) fresh.price = [priceBounds[0], Math.max(priceBounds[0], maxPrice)];
    setFilters(fresh);
    setDraft(fresh);
    setSortBy('recommended');
  }, [priceBounds, q, categoryId, maxPrice]);

  const visibleCategories = useMemo(() => categories.filter(c => products?.some(p => p.category === c._id)), [categories, products]);
  const colors = useMemo(() => [...new Set((products || []).map(p => p.color).filter(Boolean))], [products]);
  const sizes = useMemo(() => sortSizes([...new Set((products || []).flatMap(p => p.sizes || []))]), [products]);

  const results = useMemo(() => {
    const list = applyFilters(products || [], filters);
    return SORTS[sortBy].fn ? [...list].sort(SORTS[sortBy].fn) : list;
  }, [products, filters, sortBy]);

  const draftCount = useMemo(() => applyFilters(products || [], draft).length, [products, draft]);
  const activeCount = countActiveFilters(filters, priceBounds);
  const category = categories.find(c => c._id === categoryId);
  const title = category ? category.name : q ? `Results for “${q}”` : maxPrice ? `Under ₹${maxPrice}` : 'Shop All';
  const filterProps = { categories: categoryId ? [] : visibleCategories, colors, sizes, priceBounds };
  const clearAll = () => { const fresh = emptyFilters(priceBounds); setDraft(fresh); setFilters(fresh); };

  const openFilters = () => {
    setDraft(filters);
    setDrawerGroup(null);
    filterSheet.onOpen();
  };
  const drawerGroups = filterGroups({ ...filterProps, filters: draft, set: (k, v) => setDraft(d => ({ ...d, [k]: v })) });
  const activeGroup = drawerGroups.find(g => g.key === drawerGroup) || drawerGroups[0];

  const noResults = products !== null && results.length === 0;

  return (
    <Box>
      <Breadcrumb items={[{ label: 'Home', to: '/' }, ...(category || q ? [{ label: 'Shop All', to: '/search', state: {} }] : []), { label: title }]} />

      {/* Desktop toolbar */}
      <Box display={{ base: 'none', md: 'flex' }} alignItems='center' justifyContent='space-between' px='40px' py='16px' >
        <Box display='flex' alignItems='baseline' gap='16px' >
          <Text fontSize='18px' {...condensedLabel} >Filters</Text>
          {activeCount > 0 && <Text as='button' fontSize='13px' textDecoration='underline' color='blackAlpha.700' onClick={clearAll} >Clear all ({activeCount})</Text>}
        </Box>
        <Box display='flex' alignItems='center' gap='24px' >
          <Menu placement='bottom-end' >
            <MenuButton type='button' >
              <Box display='flex' alignItems='center' gap='6px' >
                <Text fontSize='18px' fontWeight={600} sx={{ fontStretch: '75%' }} >Sort By:</Text>
                <Text fontSize='12.6px' fontWeight={500} >{SORTS[sortBy].label}</Text>
                <ExpandMore sx={{ fontSize: 18 }} />
              </Box>
            </MenuButton>
            <MenuList zIndex={450} minWidth='220px' borderRadius='8px' boxShadow='popover' >
              <MenuOptionGroup type='radio' value={sortBy} onChange={setSortBy} >
                {Object.entries(SORTS).map(([key, s]) => <MenuItemOption key={key} value={key} fontSize='14px' >{s.label}</MenuItemOption>)}
              </MenuOptionGroup>
            </MenuList>
          </Menu>
          <Text fontSize='12.6px' >{products === null ? 'Loading…' : `${results.length} Results`}</Text>
        </Box>
      </Box>

      {/* Phone: title + sticky FILTER | SORT bar */}
      <Box display={{ base: 'block', md: 'none' }} px='12px' pt='14px' pb='10px' >
        <Text as='h1' fontSize='20px' fontWeight={700} sx={{ fontStretch: '75%' }} textTransform='uppercase' >{title}</Text>
        <Text fontSize='12px' color='raano.muted' >{products === null ? 'Loading…' : `${results.length} Results`}</Text>
      </Box>
      <Box display={{ base: 'flex', md: 'none' }} position='sticky' top='101px' zIndex={300} bg='raano.white' borderY='1px solid' borderColor='raano.border' >
        <Box as='button' flex={1} height='44px' fontSize='14px' {...condensedLabel} borderRight='1px solid' borderColor='raano.border' onClick={openFilters} >
          Filter{activeCount > 0 && ` (${activeCount})`}
        </Box>
        <Box as='button' flex={1} height='44px' fontSize='14px' {...condensedLabel} onClick={sortSheet.onOpen} >Sort</Box>
      </Box>

      <Box display='flex' alignItems='flex-start' gap='20px' px={{ base: '4px', md: '40px' }} pt={{ base: '12px', md: 0 }} pb={{ base: '32px', md: '56px' }} >
        <Box as='aside' aria-label='Filters' display={{ base: 'none', md: 'block' }} width='300px' flexShrink={0} position='sticky' top='138px' maxHeight='calc(100vh - 150px)' overflowY='auto' pr='8px' sx={{ scrollbarWidth: 'thin' }} >
          <ProductFilters filters={filters} onChange={setFilters} {...filterProps} />
        </Box>

        <Box flex={1} minWidth={0} >
          {products === null
            ? <ProductGrid variant='collection' products={[]} loading skeletons={6} />
            : !noResults
              ? <ProductGrid variant='collection' products={results} />
              : (
                <Box px={{ base: '8px', md: 0 }} >
                  <Box display='flex' alignItems='center' gap='10px' height='48px' px='16px' border='1px solid' borderColor='raano.border' borderRadius='searchPill' maxWidth='560px' >
                    <SearchIcon sx={{ fontSize: 20 }} />
                    <Text fontSize='15px' noOfLines={1} >{q || title}</Text>
                  </Box>
                  <Text mt='14px' fontSize='14px' color='raano.muted' >
                    {activeCount ? 'No products match these filters. Try removing some.' : `No results found for “${q || title}”. Check the spelling or use a different word or phrase.`}
                  </Text>
                  <Box display='flex' gap='10px' mt='16px' >
                    {activeCount > 0 && <Button variant='wipe' height='44px' px='24px' fontSize='15px' onClick={clearAll} >Clear filters</Button>}
                    <Button variant='ink' height='44px' px='24px' fontSize='15px' onClick={() => navigate('/search', { state: {} })} >Shop all</Button>
                  </Box>
                  <Text mt='40px' mb='16px' fontSize='18px' fontWeight={700} textTransform='uppercase' sx={{ fontStretch: '75%' }} >Products you may like</Text>
                  <ProductGrid variant='collection' products={[...allProducts].reverse().slice(0, 6)} />
                </Box>
              )}
        </Box>
      </Box>

      {/* Phone filter drawer: groups on the left, options on the right */}
      <Drawer isOpen={filterSheet.isOpen} placement='right' onClose={filterSheet.onClose} size='full' >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton top='12px' />
          <DrawerHeader fontSize='16px' {...condensedLabel} borderBottom='1px solid' borderColor='raano.border' >Filters</DrawerHeader>
          <DrawerBody p={0} display='flex' >
            <Box width='40%' bg='raano.surface' >
              {drawerGroups.map(g => (
                <Box key={g.key} as='button' width='100%' textAlign='left' px='14px' py='16px' fontSize='14px' borderBottom='1px solid' borderColor='raano.border'
                  bg={activeGroup?.key === g.key ? 'raano.white' : 'transparent'} fontWeight={activeGroup?.key === g.key ? 600 : 400} onClick={() => setDrawerGroup(g.key)} >
                  {g.title}{g.count ? ` (${g.count})` : ''}
                </Box>
              ))}
            </Box>
            <Box flex={1} px='16px' py='12px' overflowY='auto' >{activeGroup?.body}</Box>
          </DrawerBody>
          <DrawerFooter gap='10px' borderTop='1px solid' borderColor='raano.border' pb='calc(16px + env(safe-area-inset-bottom))' >
            <Button variant='wipe' flex={1} height='44px' fontSize='13px' onClick={() => setDraft(emptyFilters(priceBounds))} >Remove all filters</Button>
            <Button variant='ink' flex={1} height='44px' fontSize='13px' onClick={() => { setFilters(draft); filterSheet.onClose(); }} >Apply filters ({draftCount})</Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Phone sort sheet */}
      <Drawer isOpen={sortSheet.isOpen} placement='bottom' onClose={sortSheet.onClose} >
        <DrawerOverlay />
        <DrawerContent borderTopRadius='20px' pb='env(safe-area-inset-bottom)' >
          <DrawerHeader fontSize='16px' {...condensedLabel} >Sort by</DrawerHeader>
          <DrawerBody pb='20px' >
            {Object.entries(SORTS).map(([key, s]) => (
              <Box key={key} as='button' width='100%' display='flex' justifyContent='space-between' alignItems='center' py='14px' borderBottom='1px solid' borderColor='raano.border' fontSize='15px' fontWeight={sortBy === key ? 600 : 400}
                onClick={() => { setSortBy(key); sortSheet.onClose(); }} >
                {s.label}
                <Box width='18px' height='18px' borderRadius='full' border='1px solid' borderColor='raano.black' display='flex' alignItems='center' justifyContent='center' >
                  {sortBy === key && <Box width='10px' height='10px' borderRadius='full' bg='raano.black' />}
                </Box>
              </Box>
            ))}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default Search;
